import React, { useState, useRef, useEffect, useMemo } from "react";
import { useColors } from "../constants/Colors";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import AuthModal from "../components/AuthModal";
import EmptyState from "../components/EmptyState";
import { trpcReact, realtimeClient, queryClient } from "../trpc_client";
import FilterTags from "../components/FilterTags";
import FAQCard from "../components/chat/FAQCard";
import FlatChatCard from "../components/chat/FlatChatCard";
import HowToFlaterooCard from "../components/chat/HowToFlaterooCard";
import ApplicationChatCard from "../components/chat/ApplicationChatCard";
import AudioWaveform from "../components/chat/AudioWaveform";
import { Smile, Reply, X, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Application type for looking up application details
interface ApplicationData {
  application_id: number;
  type: "video" | "text";
  state: string;
  url?: string;
}

interface ChatUser {
  username: string;
  user_id: number;
  is_flat_member: boolean;
  profile_picture: string;
}

interface MessageState {
  message_id: number;
  user_id: number;
  state: "not_delivered" | "delivered" | "read";
  delivered_at: string | null;
  read_at: string | null;
}

interface MessageReaction {
  message_id: number;
  user_id: number;
  reaction: string;
}

interface ChatMessage {
  message_id: number;
  conversation_id: number;
  sender_id: number;
  message: string | null;
  type: string;
  created_at: string | null;
  url?: string;
  filename?: string;
  // For application messages, this indicates if it's a video or text application
  application_type?: "video" | "text";
  message_state?: MessageState[];
  message_reactions?: MessageReaction[];
  reply_to_message_id?: number | null;
}

interface Chat {
  conversation_id: number;
  application_id?: number;
  flat_name: string;
  flat_id: number;
  is_flat_member: boolean;
  flat_picture: string;
  type: string | null;
  messages: ChatMessage[];
  state: string;
  users: ChatUser[];
}

// Chat filter type
interface ChatFilters {
  text: boolean;
  video: boolean;
  accepted: boolean;
  unread: boolean;
  inactive: boolean;
}

const defaultChatFilters: ChatFilters = {
  text: false,
  video: false,
  accepted: false,
  unread: false,
  inactive: false,
};

const REACTION_OPTIONS = ["👍", "❤️", "😂", "😮", "😢"];

interface ChatsPlaceholderProps {
  mobileConversationId?: number;
}

const ChatsPlaceholder: React.FC<ChatsPlaceholderProps> = ({
  mobileConversationId,
}) => {
  const { t } = useTranslation();
  const colors = useColors();
  const { user } = useAuth();
  const { userExistsInDb } = useProfile();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [messageInput, setMessageInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [chatFilters, setChatFilters] =
    useState<ChatFilters>(defaultChatFilters);
  const [activeReactionPicker, setActiveReactionPicker] = useState<
    number | null
  >(null);
  const [replyToMessage, setReplyToMessage] = useState<ChatMessage | null>(
    null,
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const reactionPickerRef = useRef<HTMLDivElement>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const deliveredSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(
    null,
  );
  const readSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const reactionSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(
    null,
  );

  // Toggle a specific filter (only one can be active at a time)
  const toggleFilter = (key: keyof ChatFilters) => {
    setChatFilters((prev) => {
      // If this filter is already selected, deselect it (go back to "All")
      if (prev[key]) {
        return defaultChatFilters;
      }
      // Otherwise, select only this filter
      return {
        ...defaultChatFilters,
        [key]: true,
      };
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setChatFilters(defaultChatFilters);
  };

  // Get current user profile to get user_id
  const { data: currentUser } = trpcReact.getCurrentUserProfile.useQuery(
    undefined,
    {
      enabled: userExistsInDb,
    },
  );

  // Fetch chats (requires user_id from currentUser)
  const {
    data: chats,
    isLoading,
    refetch: refetchChats,
  } = trpcReact.getChats.useQuery(
    { user_id: currentUser?.user_id ?? 0 },
    {
      enabled: !!user && !!currentUser?.user_id,
    },
  );

  // Fetch user's sent applications to get application types
  const { data: userApplications } = trpcReact.getApplicationsByUser.useQuery(
    undefined,
    { enabled: userExistsInDb },
  );

  // Fetch received applications (applications to user's flats)
  const { data: receivedApplications } = trpcReact.getRoomApplications.useQuery(
    undefined,
    { enabled: userExistsInDb },
  );

  // Fetch flat profiles for sent applications (to get flat images)
  const { data: flatProfilesData } =
    trpcReact.getFlatProfilesOfAppliedFlats.useQuery(undefined, {
      enabled: userExistsInDb,
    });

  // Fetch applicant profiles for received applications (to get applicant images)
  const { data: applicantProfilesData } =
    trpcReact.getApplicantProfiles.useQuery(undefined, {
      enabled: userExistsInDb,
    });

  // Create maps for quick lookup
  const applicationsMap = useMemo(() => {
    const map: Record<number, ApplicationData> = {};
    // Add sent applications
    if (userApplications) {
      for (const app of userApplications) {
        map[app.application_id] = {
          application_id: app.application_id,
          type: app.type as "video" | "text",
          state: app.state,
          url: app.url,
        };
      }
    }
    // Add received applications
    if (receivedApplications) {
      for (const room of receivedApplications) {
        for (const app of room.applications) {
          map[app.application_id] = {
            application_id: app.application_id,
            type: app.type as "video" | "text",
            state: app.state,
            url: app.url,
          };
        }
      }
    }
    return map;
  }, [userApplications, receivedApplications]);

  const flatProfilesMap = useMemo(() => {
    if (!flatProfilesData?.flatProfiles) return {};
    const map: Record<number, { name: string; image: string | null }> = {};
    for (const profile of flatProfilesData.flatProfiles) {
      if (profile.application_id && profile.flat) {
        map[profile.application_id] = {
          name: profile.flat.name || "Unknown Flat",
          image: profile.urls?.[0] || null,
        };
      }
    }
    return map;
  }, [flatProfilesData]);

  const applicantProfilesMap = useMemo(() => {
    if (!applicantProfilesData) return {};
    const map: Record<number, { name: string; image: string | null }> = {};
    for (const profile of applicantProfilesData) {
      if (profile.application_id) {
        map[profile.application_id] = {
          name: profile.username || "Unknown",
          image: profile.profile_images?.[0] || null,
        };
      }
    }
    return map;
  }, [applicantProfilesData]);

  // Set up realtime connection and subscription
  useEffect(() => {
    if (!user) {
      realtimeClient.setIsAuthenticated(false);
      return;
    }

    realtimeClient.setIsAuthenticated(true);
  }, [user]);

  // Subscribe to realtime messages - separate effect with stable dependencies
  useEffect(() => {
    if (!user || !currentUser?.user_id) return;

    // Cleanup previous subscription if any
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    const client = realtimeClient.getClient();
    if (!client) return;

    const subscription = client.chat.conversation.subscribe(undefined, {
      onStarted: () => {
        console.log("Realtime subscription started");
      },
      onData: (msg) => {
        console.log("Received realtime message:", msg);

        // Update React Query cache directly using tRPC's query key format
        const queryKey = [
          ["getChats"],
          { input: { user_id: currentUser.user_id }, type: "query" },
        ];

        const currentData = queryClient.getQueryData<Chat[]>(queryKey);

        if (!currentData) {
          // No cached data yet, invalidate to trigger refetch
          queryClient.invalidateQueries({ queryKey: [["getChats"]] });
          return;
        }

        const chatIndex = currentData.findIndex(
          (c) => c.conversation_id === msg.conversation_id,
        );

        if (chatIndex === -1) {
          // New conversation - invalidate to trigger refetch
          queryClient.invalidateQueries({ queryKey: [["getChats"]] });
          return;
        }

        // Check if message already exists
        const chat = currentData[chatIndex];
        if (chat.messages.some((m) => m.message_id === msg.message_id)) {
          return;
        }

        // Add new message to the chat
        const updatedChats = [...currentData];
        updatedChats[chatIndex] = {
          ...chat,
          messages: [msg, ...chat.messages],
        };

        queryClient.setQueryData(queryKey, updatedChats);
      },
      onError: (err: Error) => {
        console.error("Realtime subscription error:", err);
      },
    });

    subscriptionRef.current = subscription;

    // Subscribe to delivery/read/reaction events so web stays in sync
    const deliveredSubscription = client.chat.messageDelivered.subscribe(
      undefined,
      {
        onData: () => {
          queryClient.invalidateQueries({ queryKey: [["getChats"]] });
        },
      },
    );
    deliveredSubscriptionRef.current = deliveredSubscription;

    const readSubscription = client.chat.messageRead.subscribe(undefined, {
      onData: () => {
        queryClient.invalidateQueries({ queryKey: [["getChats"]] });
      },
    });
    readSubscriptionRef.current = readSubscription;

    const reactionSubscription = client.chat.messageReaction.subscribe(
      undefined,
      {
        onData: () => {
          queryClient.invalidateQueries({ queryKey: [["getChats"]] });
        },
      },
    );
    reactionSubscriptionRef.current = reactionSubscription;

    return () => {
      subscription.unsubscribe();
      deliveredSubscription.unsubscribe();
      readSubscription.unsubscribe();
      reactionSubscription.unsubscribe();
      subscriptionRef.current = null;
      deliveredSubscriptionRef.current = null;
      readSubscriptionRef.current = null;
      reactionSubscriptionRef.current = null;
    };
  }, [user, currentUser?.user_id]); // Minimal stable dependencies

  const markMessagesAsDeliveredMutation =
    trpcReact.markMessagesAsDelivered.useMutation();
  const markMessagesAsReadMutation = trpcReact.markMessagesAsRead.useMutation();
  const sendReactionMutation = trpcReact.sendMessageReaction.useMutation({
    onMutate: async (variables) => {
      if (!currentUser?.user_id) return;

      const queryKey = [
        ["getChats"],
        { input: { user_id: currentUser.user_id }, type: "query" },
      ];

      await queryClient.cancelQueries({ queryKey: [["getChats"]] });

      const previousData = queryClient.getQueryData<Chat[]>(queryKey);
      if (!previousData) return;

      const updatedChats = previousData.map((chat) => {
        const messageIndex = chat.messages.findIndex(
          (m) => m.message_id === variables.message_id,
        );
        if (messageIndex === -1) return chat;

        const message = chat.messages[messageIndex];
        const reactions = message.message_reactions || [];
        const existingReactionIndex = reactions.findIndex(
          (r) => r.user_id === currentUser.user_id,
        );

        let updatedReactions: MessageReaction[];

        if (existingReactionIndex !== -1) {
          const existing = reactions[existingReactionIndex];
          if (existing.reaction === variables.reaction) {
            // Same reaction: toggle off (remove)
            updatedReactions = reactions.filter(
              (_, i) => i !== existingReactionIndex,
            );
          } else {
            // Different reaction: update
            updatedReactions = reactions.map((r, i) =>
              i === existingReactionIndex
                ? { ...r, reaction: variables.reaction }
                : r,
            );
          }
        } else {
          // No existing reaction: add new one
          updatedReactions = [
            ...reactions,
            {
              message_id: variables.message_id,
              user_id: currentUser.user_id,
              reaction: variables.reaction,
            },
          ];
        }

        const updatedMessages = [...chat.messages];
        updatedMessages[messageIndex] = {
          ...message,
          message_reactions: updatedReactions,
        };

        return { ...chat, messages: updatedMessages };
      });

      queryClient.setQueryData(queryKey, updatedChats);

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      // Roll back to previous data on error
      if (context?.previousData && currentUser?.user_id) {
        const queryKey = [
          ["getChats"],
          { input: { user_id: currentUser.user_id }, type: "query" },
        ];
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      // Always refetch after mutation settles to sync with server
      queryClient.invalidateQueries({ queryKey: [["getChats"]] });
    },
  });

  // Send message mutation with optimistic update
  const sendMessageMutation = trpcReact.sendTextMessage.useMutation({
    onMutate: async (variables) => {
      // Optimistically add the message to the cache
      if (!currentUser?.user_id || !selectedChat) return;

      const queryKey = [
        ["getChats"],
        { input: { user_id: currentUser.user_id }, type: "query" },
      ];

      // Create an optimistic message
      const optimisticMessage: ChatMessage = {
        message_id: -Date.now(), // Temporary negative ID
        conversation_id: variables.conversation_id,
        sender_id: currentUser.user_id,
        message: variables.message,
        type: "text",
        created_at: variables.created_at,
        reply_to_message_id: variables.reply_to_message_id,
      };

      const currentData = queryClient.getQueryData<Chat[]>(queryKey);
      if (!currentData) return;

      const chatIndex = currentData.findIndex(
        (c) => c.conversation_id === variables.conversation_id,
      );
      if (chatIndex === -1) return;

      const updatedChats = [...currentData];
      updatedChats[chatIndex] = {
        ...currentData[chatIndex],
        messages: [optimisticMessage, ...currentData[chatIndex].messages],
      };

      queryClient.setQueryData(queryKey, updatedChats);

      return {
        previousData: currentData,
        optimisticMessageId: optimisticMessage.message_id,
      };
    },
    onSuccess: (data, variables, context) => {
      setMessageInput("");

      // Replace optimistic message with real one from server
      if (!currentUser?.user_id || !context) return;

      const queryKey = [
        ["getChats"],
        { input: { user_id: currentUser.user_id }, type: "query" },
      ];

      const currentData = queryClient.getQueryData<Chat[]>(queryKey);
      if (!currentData) return;

      const chatIndex = currentData.findIndex(
        (c) => c.conversation_id === variables.conversation_id,
      );
      if (chatIndex === -1) return;

      const updatedChats = [...currentData];
      const chat = currentData[chatIndex];

      // Remove the optimistic message and add the real one
      const messagesWithoutOptimistic = chat.messages.filter(
        (m) => m.message_id !== context.optimisticMessageId,
      );

      updatedChats[chatIndex] = {
        ...chat,
        messages: [
          {
            message_id: data.message_id,
            conversation_id: data.conversation_id,
            sender_id: data.sender_id,
            message: data.message,
            type: data.type,
            created_at: data.created_at?.toString() || null,
            reply_to_message_id: data.reply_to_message_id,
          },
          ...messagesWithoutOptimistic,
        ],
      };

      queryClient.setQueryData(queryKey, updatedChats);
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousData && currentUser?.user_id) {
        const queryKey = [
          ["getChats"],
          { input: { user_id: currentUser.user_id }, type: "query" },
        ];
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
  });

  // Keep delivery state in sync for incoming messages
  useEffect(() => {
    if (!currentUser?.user_id || !chats?.length) return;
    const toMarkDelivered = chats
      .flatMap((chat) => chat.messages)
      .filter(
        (message) =>
          message.sender_id !== currentUser.user_id &&
          !isMessageDeliveredToUser(
            message as ChatMessage,
            currentUser.user_id,
          ),
      )
      .map((message) => ({
        message_id: message.message_id,
        delivered_at: new Date().toISOString(),
      }));

    if (
      toMarkDelivered.length > 0 &&
      !markMessagesAsDeliveredMutation.isPending
    ) {
      markMessagesAsDeliveredMutation.mutate(toMarkDelivered);
    }
  }, [
    chats,
    currentUser?.user_id,
    markMessagesAsDeliveredMutation,
    markMessagesAsDeliveredMutation.isPending,
  ]);

  // Mark visible conversation messages as read
  useEffect(() => {
    if (!currentUser?.user_id || !selectedChat) return;
    const toMarkRead = selectedChat.messages
      .filter(
        (message) =>
          message.sender_id !== currentUser.user_id &&
          !isMessageReadByUser(message, currentUser.user_id),
      )
      .map((message) => ({
        message_id: message.message_id,
        read_at: new Date().toISOString(),
      }));

    if (toMarkRead.length > 0 && !markMessagesAsReadMutation.isPending) {
      markMessagesAsReadMutation.mutate(toMarkRead);
    }
  }, [
    selectedChat,
    currentUser?.user_id,
    markMessagesAsReadMutation,
    markMessagesAsReadMutation.isPending,
  ]);

  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        reactionPickerRef.current &&
        !reactionPickerRef.current.contains(event.target as Node)
      ) {
        setActiveReactionPicker(null);
      }
    };
    if (activeReactionPicker !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeReactionPicker]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedChat?.messages]);

  // Auto-select conversation when mobileConversationId is provided
  useEffect(() => {
    if (mobileConversationId && chats && chats.length > 0) {
      const chat = (chats as Chat[]).find(
        (c) => c.conversation_id === mobileConversationId,
      );
      if (chat) setSelectedChat(chat);
    }
  }, [mobileConversationId, chats]);

  // Update selected chat when chats data changes
  useEffect(() => {
    if (selectedChat && chats && chats.length > 0) {
      const updatedChat = chats.find(
        (c) => c.conversation_id === selectedChat.conversation_id,
      );
      if (updatedChat) {
        setSelectedChat(updatedChat as Chat);
      }
    }
  }, [chats, selectedChat?.conversation_id]);

  const handleSendMessage = () => {
    if (!messageInput.trim() || !selectedChat || isInputLockedForApplicant)
      return;

    sendMessageMutation.mutate({
      conversation_id: selectedChat.conversation_id,
      message: messageInput.trim(),
      created_at: new Date().toISOString(),
      ...(replyToMessage
        ? { reply_to_message_id: replyToMessage.message_id }
        : {}),
    });
    setReplyToMessage(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: string | null) => {
    if (!date) return "";
    return new Date(date).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (date: string | null) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  };

  const getLastMessage = (chat: Chat) => {
    if (chat.messages.length === 0) return "No messages yet";
    const lastMsg = chat.messages[0]; // messages are ordered desc
    if (lastMsg.type === "application") return "Application";
    if (lastMsg.type === "image") return "📷 Image";
    if (lastMsg.type === "audio") return "🎤 Voice message";
    if (lastMsg.type === "location") return "📍 Location";
    return lastMsg.message || "";
  };

  const isSupportAccount = currentUser?.user_id === 1;

  const getChatTitle = (chat: Chat) => {
    // FAQ Chat
    if (chat.type === "faq_chat") {
      if (isSupportAccount) {
        // Support account sees the other user's name
        const otherUser = chat.users.find(
          (u) => !u.is_flat_member && u.user_id !== 1,
        );
        return otherUser?.username || "Unknown User";
      }
      return "Niki & Lorin | FAQ Chat";
    }
    // WG/Flat Chat
    if (chat.type === "flat_chat") {
      return chat.flat_name;
    }
    // Regular application chats
    if (chat.is_flat_member) {
      // User is flat member, show applicant name
      const applicant = chat.users.find((u) => !u.is_flat_member);
      return applicant?.username || chat.flat_name;
    }
    // User is applicant, show flat name
    return chat.flat_name;
  };

  const getChatImage = (chat: Chat) => {
    // FAQ Chat
    if (chat.type === "faq_chat") {
      if (isSupportAccount) {
        // Support account sees the other user's profile picture
        const otherUser = chat.users.find(
          (u) => !u.is_flat_member && u.user_id !== 1,
        );
        return otherUser?.profile_picture || "";
      }
      return "/f_only.png";
    }
    // WG/Flat Chat - show flat picture
    if (chat.type === "flat_chat") {
      return chat.flat_picture;
    }
    // Regular chats
    if (chat.is_flat_member) {
      const applicant = chat.users.find(
        (u) => !u.is_flat_member && u.user_id !== 1,
      );
      return applicant?.profile_picture || "";
    }
    return chat.flat_picture;
  };

  // Get border radius based on chat type (more square for flat/WG chats)
  const getChatImageStyle = (chat: Chat) => {
    if (chat.type === "faq_chat") {
      if (isSupportAccount) {
        return "rounded-full"; // Circular for user profile pictures
      }
      return "rounded-xl border-2 !object-contain p-1"; // Square with accent border, contain image
    }
    if (chat.type === "flat_chat") {
      return "rounded-xl"; // More square (12px radius)
    }
    return "rounded-full"; // Circular for regular chats
  };

  // Get additional inline styles for chat image (for border color)
  const getChatImageInlineStyle = (chat: Chat): React.CSSProperties => {
    if (chat.type === "faq_chat" && !isSupportAccount) {
      return {
        borderColor: colors.accentColor,
        backgroundColor: colors.containerNeutralLightLowest,
      };
    }
    return {};
  };

  const isMessageReadByUser = (message: ChatMessage, userId?: number) => {
    if (!userId || !message.message_state?.length) return false;
    return message.message_state.some(
      (state) => state.user_id === userId && state.state === "read",
    );
  };

  const isMessageDeliveredToUser = (message: ChatMessage, userId?: number) => {
    if (!userId || !message.message_state?.length) return false;
    return message.message_state.some(
      (state) =>
        state.user_id === userId &&
        (state.state === "delivered" || state.state === "read"),
    );
  };

  const hasUnreadMessages = (chat: Chat, userId?: number) => {
    if (!userId) return false;
    if (chat.state === "archived") return false;
    return chat.messages.some(
      (message) =>
        message.sender_id !== userId && !isMessageReadByUser(message, userId),
    );
  };

  const getRelevantRecipientIdsForMessageState = (
    chat: Chat,
    message: ChatMessage,
  ): number[] => {
    const sender = chat.users.find(
      (user) => user.user_id === message.sender_id,
    );
    const nonSenderIds = chat.users
      .filter((user) => user.user_id !== message.sender_id)
      .map((user) => user.user_id);

    // For FAQ/flat chats, use all non-sender participants.
    if (chat.type !== "room_application") {
      return nonSenderIds;
    }

    // For room applications, mirror native behavior:
    // - flat member sender -> track applicant states
    // - applicant sender -> track flat member states
    const flatMemberIds = chat.users
      .filter((user) => user.is_flat_member)
      .map((user) => user.user_id);
    const applicantIds = chat.users
      .filter((user) => !user.is_flat_member)
      .map((user) => user.user_id);

    if (!sender) return nonSenderIds;
    return sender.is_flat_member ? applicantIds : flatMemberIds;
  };

  const getOwnMessageState = (
    chat: Chat,
    message: ChatMessage,
  ): "sent" | "delivered" | "read" => {
    if (!message.message_state?.length) return "sent";

    const relevantRecipientIds = getRelevantRecipientIdsForMessageState(
      chat,
      message,
    );
    if (!relevantRecipientIds.length) return "sent";

    const isRead = message.message_state.some(
      (state) =>
        relevantRecipientIds.includes(state.user_id) && state.state === "read",
    );
    if (isRead) return "read";

    const isDelivered = message.message_state.some(
      (state) =>
        relevantRecipientIds.includes(state.user_id) &&
        (state.state === "delivered" || state.state === "read"),
    );
    if (isDelivered) return "delivered";

    return "sent";
  };

  const hasFlatSideFirstReply = (chat: Chat) => {
    if (chat.type !== "room_application") return true;
    return chat.messages.some((message) => {
      if (message.type === "application") return false;
      const sender = chat.users.find((u) => u.user_id === message.sender_id);
      return Boolean(sender?.is_flat_member);
    });
  };

  const isInputLockedForApplicant =
    selectedChat?.type === "room_application" &&
    !selectedChat.is_flat_member &&
    !hasFlatSideFirstReply(selectedChat);

  // Filter chats based on search query and filters
  const filteredChats = useMemo(() => {
    if (!chats || !chats.length) return [];

    let filtered = chats as Chat[];

    // Apply filters
    const anyFilterActive = Object.values(chatFilters).some((v) => v);

    if (anyFilterActive) {
      filtered = filtered.filter((chat) => {
        // Inactive filter (archived chats)
        if (chatFilters.inactive && chat.state === "archived") return true;

        // Skip archived chats if inactive filter is not set
        if (chat.state === "archived") return false;

        // Get the application type from the applicationsMap
        const applicationId = chat.application_id;
        const applicationType = applicationId
          ? applicationsMap[applicationId]?.type
          : null;

        // Text application filter
        if (chatFilters.text) {
          if (applicationType === "text") return true;
        }

        // Video application filter
        if (chatFilters.video) {
          if (applicationType === "video") return true;
        }

        // Accepted filter - check application state
        if (chatFilters.accepted) {
          const applicationState = applicationId
            ? applicationsMap[applicationId]?.state
            : null;
          if (applicationState === "accepted") return true;
        }

        // Unread filter - would need unread count tracking
        if (chatFilters.unread) {
          return hasUnreadMessages(chat, currentUser?.user_id);
        }

        return false;
      });
    } else {
      // No filters active - hide archived by default
      filtered = filtered.filter((chat) => chat.state !== "archived");
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((chat) => {
        // Search in chat title (name)
        const title = getChatTitle(chat).toLowerCase();
        if (title.includes(query)) return true;

        // Search in messages
        const hasMatchingMessage = chat.messages.some(
          (msg) => msg.message && msg.message.toLowerCase().includes(query),
        );
        if (hasMatchingMessage) return true;

        return false;
      });
    }

    // Sort by last message date (most recent first)
    filtered.sort((a, b) => {
      // Get the last message date for each chat (messages are already sorted desc, so first is newest)
      const aLastMessage = a.messages[0]?.created_at;
      const bLastMessage = b.messages[0]?.created_at;

      // Chats with no messages go to the end
      if (!aLastMessage && !bLastMessage) return 0;
      if (!aLastMessage) return 1;
      if (!bLastMessage) return -1;

      // Sort by date descending (newest first)
      return (
        new Date(bLastMessage).getTime() - new Date(aLastMessage).getTime()
      );
    });

    return filtered;
  }, [chats, searchQuery, chatFilters, applicationsMap, currentUser?.user_id]);

  // Show sign-in prompt if not authenticated
  if (!user) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ backgroundColor: colors.containerNeutralSolidSurface }}
      >
        <EmptyState
          type="no-chats"
          title={t("chats_tab.tabbar_title") || "Chats"}
          description="Sign in to view your messages and start conversations."
          action={{
            label: t("sign_in") || "Sign in",
            onClick: () => setShowAuth(true),
          }}
        />
        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          onSuccess={() => setShowAuth(false)}
        />
      </div>
    );
  }

  return (
    <div
      className="h-screen flex overflow-hidden "
      style={{ backgroundColor: colors.containerNeutralSolidSurface }}
    >
      {/* Chat List Sidebar — hidden when showing a single conversation on mobile */}
      {!mobileConversationId && (
        <div
          className="w-full lg:w-[480px] flex flex-col h-full min-w-0 overflow-hidden"
          style={{
            backgroundColor: colors.containerNeutralSolidSurface,
            boxShadow: "1px 0 0 rgba(2, 2, 16, 0.06)",
          }}
        >
          <div className="pt-3 flex-shrink-0 overflow-hidden">
            <h1
              className="text-lg font-semibold mb-2 mx-4"
              style={{ color: colors.textNeutralDefault }}
            >
              {t("chats_tab.tabbar_title") || "Chats"}
            </h1>
            <div className="relative mx-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="absolute left-4 top-1/2 -translate-y-1/2"
                style={{ color: colors.textNeutralMuted }}
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.3-4.3"></path>
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search chats..."
                className="w-full pl-10 pr-10 py-2.5 rounded-full text-sm outline-none"
                style={{
                  backgroundColor: colors.containerNeutralLightLowest,
                  color: colors.textNeutralDefault,
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:opacity-70"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: colors.textNeutralMuted }}
                  >
                    <path d="M18 6 6 18"></path>
                    <path d="m6 6 12 12"></path>
                  </svg>
                </button>
              )}
            </div>

            {/* Filter Tags */}
            <div className="mt-2">
              <FilterTags
                horizontalPadding
                filters={[
                  {
                    id: "unread",
                    label: t("filter_tags.unread") || "Unread",
                    isSelected: chatFilters.unread,
                    onPress: () => toggleFilter("unread"),
                  },
                  {
                    id: "text",
                    label: t("filter_tags.text") || "Text",
                    isSelected: chatFilters.text,
                    onPress: () => toggleFilter("text"),
                  },
                  {
                    id: "video",
                    label: t("filter_tags.video") || "Video",
                    isSelected: chatFilters.video,
                    onPress: () => toggleFilter("video"),
                  },
                  {
                    id: "inactive",
                    label: t("filter_tags.inactive") || "Archived",
                    isSelected: chatFilters.inactive,
                    onPress: () => toggleFilter("inactive"),
                  },
                ]}
                onPressAll={resetFilters}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {isLoading && (
              <div
                className="p-4 text-center"
                style={{ color: colors.textNeutralMuted }}
              >
                Loading chats...
              </div>
            )}

            {!isLoading && (!filteredChats || filteredChats.length === 0) && (
              <div
                className="p-4 text-center"
                style={{ color: colors.textNeutralMuted }}
              >
                {searchQuery ? "No results found" : "No conversations yet"}
              </div>
            )}

            {filteredChats?.map((chat) => {
              const isSelected =
                selectedChat?.conversation_id === chat.conversation_id;
              const isUnread = hasUnreadMessages(
                chat as Chat,
                currentUser?.user_id,
              );
              return (
                <div
                  key={chat.conversation_id}
                  onClick={() => {
                    setSelectedChat(chat as Chat);
                    if (window.innerWidth < 1024) {
                      navigate(`/chats/${chat.conversation_id}`);
                    }
                  }}
                  className="py-3 mx-3 cursor-pointer transition-colors rounded-xl"
                  style={{
                    backgroundColor: isSelected
                      ? colors.hoverBackground
                      : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor =
                        colors.hoverBackground;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  <div className="flex items-center gap-3 px-2.5">
                    {getChatImage(chat as Chat) ? (
                      <img
                        src={getChatImage(chat as Chat)}
                        alt=""
                        className={`w-11 h-11 object-cover ${getChatImageStyle(
                          chat as Chat,
                        )}`}
                        style={getChatImageInlineStyle(chat as Chat)}
                      />
                    ) : (
                      <div
                        className={`w-11 h-11 flex items-center justify-center text-lg font-semibold ${getChatImageStyle(
                          chat as Chat,
                        )}`}
                        style={{
                          backgroundColor: colors.accentColor,
                          color: colors.textNeutralOnPrimary,
                          ...getChatImageInlineStyle(chat as Chat),
                        }}
                      >
                        {getChatTitle(chat as Chat)
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <div className="flex justify-between items-center gap-2">
                        <p
                          className={`truncate m-0 leading-none text-[15px] ${
                            isUnread ? "font-semibold" : "font-medium"
                          }`}
                          style={{ color: colors.textNeutralDefault }}
                        >
                          {getChatTitle(chat as Chat)}
                        </p>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {isUnread && (
                            <span
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: colors.accentColor }}
                            />
                          )}
                          <span
                            className="text-[11px] leading-none"
                            style={{ color: colors.textNeutralMuted }}
                          >
                            {chat.messages[0] &&
                              formatDate(chat.messages[0].created_at)}
                          </span>
                        </div>
                      </div>
                      <p
                        className={`text-[13px] truncate m-0 leading-none mt-1.5 ${
                          isUnread ? "font-medium" : ""
                        }`}
                        style={{ color: colors.textNeutralMuted }}
                      >
                        {getLastMessage(chat as Chat)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Chat Messages Area — hidden on mobile unless mobileConversationId */}
      <div
        className={`flex-1 flex flex-col h-full min-h-0 ${mobileConversationId ? "" : "hidden lg:flex"}`}
      >
        {!selectedChat ? (
          <div
            className="flex-1 flex items-center justify-center"
            style={{ color: colors.textNeutralMuted }}
          >
            {mobileConversationId
              ? isLoading
                ? "Loading..."
                : "Conversation not found"
              : "Select a conversation to start messaging"}
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div
              className="px-5 py-3.5 flex items-center gap-3.5 flex-shrink-0"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.85)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                boxShadow: "0 1px 0 rgba(2, 2, 16, 0.06)",
              }}
            >
              {mobileConversationId && (
                <button
                  onClick={() => navigate("/chats")}
                  className="p-1.5 -ml-1.5 rounded-full hover:bg-black/5 transition-colors flex-shrink-0"
                >
                  <ArrowLeft
                    size={20}
                    style={{ color: colors.textNeutralDefault }}
                  />
                </button>
              )}
              {getChatImage(selectedChat) ? (
                <img
                  src={getChatImage(selectedChat)}
                  alt=""
                  className={`w-11 h-11 object-cover ring-2 ring-white/60 ${getChatImageStyle(
                    selectedChat,
                  )}`}
                  style={getChatImageInlineStyle(selectedChat)}
                />
              ) : (
                <div
                  className={`w-11 h-11 flex items-center justify-center text-base font-gnarly-semibold ring-2 ring-white/60 ${getChatImageStyle(
                    selectedChat,
                  )}`}
                  style={{
                    backgroundColor: colors.accentColor,
                    color: colors.textNeutralOnPrimary,
                    ...getChatImageInlineStyle(selectedChat),
                  }}
                >
                  {getChatTitle(selectedChat).charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex flex-col justify-center min-w-0 flex-1">
                <p
                  className="font-gnarly-semibold m-0 leading-none text-[15px] truncate"
                  style={{ color: colors.textNeutralDefault }}
                >
                  {getChatTitle(selectedChat)}
                </p>
                <p
                  className="text-xs m-0 leading-none mt-1.5"
                  style={{ color: colors.textNeutralMuted }}
                >
                  {selectedChat.users.length}{" "}
                  {t("participants") || "participants"}
                </p>
              </div>
            </div>

            {/* Messages + Input wrapper */}
            <div className="flex-1 overflow-y-auto min-h-0 relative flex flex-col">
              <div className="flex-1 p-4">
                {/* Default message card based on chat type */}
                {selectedChat.type === "faq_chat" && <FAQCard />}
                {selectedChat.type === "flat_chat" && <FlatChatCard />}
                {selectedChat.type === "room_application" && (
                  <>
                    <HowToFlaterooCard
                      messages={selectedChat.messages}
                      state={selectedChat.state}
                    />
                    {/* Show the application card after the greeting */}
                    {(() => {
                      // Get the application data using the chat's application_id
                      const applicationId = selectedChat.application_id;
                      if (!applicationId) return null;

                      const applicationData = applicationsMap[applicationId];
                      const applicationType = applicationData?.type || "text";

                      // Get the correct image based on whether user is flat member
                      // - Flat member sees applicant's image
                      // - Applicant sees flat's image
                      const imageUrl = selectedChat.is_flat_member
                        ? applicantProfilesMap[applicationId]?.image
                        : flatProfilesMap[applicationId]?.image;

                      const displayName = selectedChat.is_flat_member
                        ? applicantProfilesMap[applicationId]?.name
                        : flatProfilesMap[applicationId]?.name;

                      // Find application message for text content and timestamp
                      const applicationMessage = selectedChat.messages.find(
                        (m) => m.type === "application",
                      );

                      // Get reactions for the application message
                      const appReactions =
                        applicationMessage?.message_reactions || [];
                      const appGroupedReactions = Object.entries(
                        appReactions.reduce(
                          (acc, reaction) => {
                            acc[reaction.reaction] =
                              (acc[reaction.reaction] || 0) + 1;
                            return acc;
                          },
                          {} as Record<string, number>,
                        ),
                      );

                      return (
                        <div className="flex justify-end group relative">
                          {/* Hover actions - left side of card (toward center) */}
                          {applicationMessage && (
                            <div
                              className={`flex flex-col items-center gap-0.5 self-center mr-1 opacity-0 group-hover:opacity-100 transition-opacity ${activeReactionPicker === applicationMessage.message_id ? "!opacity-100" : ""}`}
                            >
                              <button
                                onClick={() =>
                                  setActiveReactionPicker(
                                    activeReactionPicker ===
                                      applicationMessage.message_id
                                      ? null
                                      : applicationMessage.message_id,
                                  )
                                }
                                className="p-1 rounded-full hover:bg-black/5 transition-colors"
                                title="React"
                              >
                                <Smile
                                  size={16}
                                  style={{ color: colors.textNeutralMuted }}
                                />
                              </button>
                              <button
                                onClick={() =>
                                  setReplyToMessage(applicationMessage)
                                }
                                className="p-1 rounded-full hover:bg-black/5 transition-colors"
                                title="Reply"
                              >
                                <Reply
                                  size={16}
                                  style={{ color: colors.textNeutralMuted }}
                                />
                              </button>
                            </div>
                          )}
                          <div className="relative">
                            <div className="w-72 sm:w-80">
                              <ApplicationChatCard
                                applicationText={
                                  applicationMessage?.message || null
                                }
                                isFlatMember={selectedChat.is_flat_member}
                                flatName={displayName || selectedChat.flat_name}
                                flatId={selectedChat.flat_id}
                                flatPicture={
                                  imageUrl || selectedChat.flat_picture
                                }
                                users={selectedChat.users}
                                applicationType={applicationType}
                                videoUrl={applicationData?.url}
                                createdAt={applicationMessage?.created_at}
                              />
                            </div>
                            {/* Reaction picker */}
                            {applicationMessage &&
                              activeReactionPicker ===
                                applicationMessage.message_id && (
                                <div
                                  ref={reactionPickerRef}
                                  className="absolute z-20 flex items-center gap-1.5 px-2 py-1.5 rounded-full left-0"
                                  style={{
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                    backdropFilter: "blur(16px)",
                                    WebkitBackdropFilter: "blur(16px)",
                                    boxShadow:
                                      "0 4px 24px rgba(2, 2, 16, 0.12), 0 0 0 1px rgba(2, 2, 16, 0.05)",
                                    bottom: "100%",
                                    marginBottom: 6,
                                    animation:
                                      "scaleIn 0.15s cubic-bezier(0.16, 1, 0.3, 1) forwards",
                                    transformOrigin: "bottom left",
                                  }}
                                >
                                  {REACTION_OPTIONS.map((reaction) => (
                                    <button
                                      key={`${applicationMessage.message_id}-picker-${reaction}`}
                                      onClick={() => {
                                        sendReactionMutation.mutate({
                                          message_id:
                                            applicationMessage.message_id,
                                          reaction,
                                        });
                                        setActiveReactionPicker(null);
                                      }}
                                      className="w-9 h-9 flex items-center justify-center rounded-full text-lg transition-all duration-150 hover:scale-125 hover:bg-black/5 active:scale-100"
                                    >
                                      {reaction}
                                    </button>
                                  ))}
                                </div>
                              )}
                            {/* Reactions at bottom-left edge */}
                            {appGroupedReactions.length > 0 && (
                              <div className="absolute -bottom-3 left-1 flex flex-wrap gap-1 z-10">
                                {appGroupedReactions.map(
                                  ([reaction, count]) => (
                                    <button
                                      key={`app-${applicationMessage?.message_id}-${reaction}`}
                                      onClick={() =>
                                        applicationMessage &&
                                        sendReactionMutation.mutate({
                                          message_id:
                                            applicationMessage.message_id,
                                          reaction,
                                        })
                                      }
                                      className="px-2 py-0.5 rounded-full text-xs transition-opacity hover:opacity-80"
                                      style={{
                                        backgroundColor:
                                          colors.containerNeutralLightLowest,
                                        color: colors.textNeutralDefault,
                                      }}
                                    >
                                      {reaction} {count}
                                    </button>
                                  ),
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}

                {(() => {
                  const reversedMessages = [...selectedChat.messages].reverse();
                  return reversedMessages.map((msg, idx) => {
                    const isOwnMessage = msg.sender_id === currentUser?.user_id;
                    const sender = selectedChat.users.find(
                      (u) => u.user_id === msg.sender_id,
                    );
                    const groupedReactions = Object.entries(
                      (msg.message_reactions || []).reduce(
                        (acc, reaction) => {
                          acc[reaction.reaction] =
                            (acc[reaction.reaction] || 0) + 1;
                          return acc;
                        },
                        {} as Record<string, number>,
                      ),
                    );
                    const ownMessageState = isOwnMessage
                      ? getOwnMessageState(selectedChat, msg)
                      : "sent";

                    // Compute shouldRoundBottom: true when next message is from a different sender,
                    // has reactions, or this is the last message
                    const nextMsg = reversedMessages[idx + 1];
                    const shouldRoundBottom =
                      !nextMsg ||
                      nextMsg.sender_id !== msg.sender_id ||
                      (msg.message_reactions &&
                        msg.message_reactions.length > 0);

                    // Show sender name only at the top of a message group
                    const prevMsg = reversedMessages[idx - 1];
                    const isFirstInGroup =
                      !prevMsg || prevMsg.sender_id !== msg.sender_id;

                    // Resolve replied-to message
                    const repliedMessage = msg.reply_to_message_id
                      ? reversedMessages.find(
                          (m) => m.message_id === msg.reply_to_message_id,
                        )
                      : null;
                    const repliedSender = repliedMessage
                      ? selectedChat.users.find(
                          (u) => u.user_id === repliedMessage.sender_id,
                        )
                      : null;

                    // Skip application messages - they're rendered with the ApplicationChatCard above
                    if (msg.type === "application") {
                      return null;
                    }

                    return (
                      <div
                        key={msg.message_id}
                        className={`flex ${
                          isOwnMessage ? "justify-end" : "justify-start"
                        }`}
                        style={{ marginBottom: shouldRoundBottom ? 12 : 2 }}
                      >
                        <div
                          className={`max-w-[70%] group relative flex ${isOwnMessage ? "" : "flex-row"}`}
                        >
                          {/* Profile picture spacer/avatar for received messages */}
                          {!isOwnMessage && (
                            <div
                              className="flex flex-col justify-end mr-2 flex-shrink-0"
                              style={{ width: 28 }}
                            >
                              {shouldRoundBottom ? (
                                sender?.profile_picture ? (
                                  <img
                                    src={sender.profile_picture}
                                    alt={sender.username || ""}
                                    className="w-7 h-7 rounded-full object-cover"
                                  />
                                ) : (
                                  <div
                                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                                    style={{
                                      backgroundColor: colors.accentColor,
                                    }}
                                  >
                                    {sender?.username
                                      ?.charAt(0)
                                      .toUpperCase() || "?"}
                                  </div>
                                )
                              ) : (
                                <div style={{ width: 28 }} />
                              )}
                            </div>
                          )}
                          <div className="min-w-0">
                            {/* Message bubble row with hover actions */}
                            <div
                              className={`flex items-center gap-1 ${isOwnMessage ? "flex-row-reverse" : "flex-row"}`}
                            >
                              <div
                                className={`rounded-2xl relative max-w-md break-words ${msg.type === "image" && !repliedMessage ? "p-0 overflow-hidden" : "px-4 py-2"}`}
                                style={{
                                  backgroundColor: isOwnMessage
                                    ? colors.accentColor
                                    : colors.containerNeutralSolidMediumLow,
                                  color: isOwnMessage
                                    ? colors.textNeutralOnPrimary
                                    : colors.textNeutralDefault,
                                }}
                              >
                                {/* Reply preview */}
                                {repliedMessage && (
                                  <div
                                    className="rounded-lg overflow-hidden mb-1.5 cursor-pointer"
                                    style={{
                                      backgroundColor: isOwnMessage
                                        ? "rgba(0,0,0,0.15)"
                                        : "rgba(0,0,0,0.06)",
                                    }}
                                  >
                                    <div className="flex">
                                      <div
                                        className="w-1 flex-shrink-0 self-stretch"
                                        style={{
                                          backgroundColor: isOwnMessage
                                            ? colors.chatRightAccentColor
                                            : colors.chatLeftAccentColor,
                                        }}
                                      />
                                      <div className="px-2.5 py-1.5 min-w-0">
                                        <p
                                          className="text-xs font-semibold m-0"
                                          style={{
                                            color: isOwnMessage
                                              ? colors.chatRightAccentColor
                                              : colors.chatLeftAccentColor,
                                          }}
                                        >
                                          {repliedMessage.sender_id ===
                                          currentUser?.user_id
                                            ? "You"
                                            : repliedSender?.username ||
                                              "Unknown"}
                                        </p>
                                        <p
                                          className="text-xs m-0 truncate max-w-[200px]"
                                          style={{
                                            color: isOwnMessage
                                              ? "rgba(255,255,255,0.7)"
                                              : colors.textNeutralMuted,
                                          }}
                                        >
                                          {repliedMessage.type === "application"
                                            ? `📋 ${t("chats_tab.last_message_types.application")}`
                                            : repliedMessage.type === "image"
                                              ? "📷 Photo"
                                              : repliedMessage.type === "audio"
                                                ? "🎤 Voice message"
                                                : repliedMessage.type ===
                                                    "document"
                                                  ? "📄 Document"
                                                  : repliedMessage.type ===
                                                      "location"
                                                    ? "📍 Location"
                                                    : repliedMessage.message ||
                                                      "Message"}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {msg.type === "image" && msg.url && (
                                  <img
                                    src={msg.url}
                                    alt=""
                                    className="max-w-[240px] rounded-2xl block"
                                  />
                                )}
                                {msg.type === "audio" && msg.url && (
                                  <AudioWaveform
                                    url={msg.url}
                                    waveformData={
                                      msg.message
                                        ? (() => {
                                            try {
                                              return JSON.parse(msg.message);
                                            } catch {
                                              return [];
                                            }
                                          })()
                                        : []
                                    }
                                    durationMs={
                                      msg.filename
                                        ? parseInt(msg.filename)
                                        : undefined
                                    }
                                    isOwnMessage={isOwnMessage}
                                  />
                                )}
                                {msg.type === "document" && msg.url && (
                                  <a
                                    href={msg.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 underline"
                                  >
                                    <span>📄</span>
                                    <span>{msg.filename || "Document"}</span>
                                  </a>
                                )}
                                {msg.type === "location" && (
                                  <p className="m-0">📍 Location shared</p>
                                )}
                                {msg.message &&
                                  msg.type !== "audio" &&
                                  msg.type !== "location" && (
                                    <span>
                                      {msg.message}
                                      <span className="inline-block w-[70px]" />
                                    </span>
                                  )}
                                <span
                                  className={`flex items-center gap-0.5 text-[10px] leading-none ${msg.type === "image" ? "absolute bottom-1.5 right-2 bg-black/40 px-1.5 py-0.5 rounded-full" : "absolute bottom-1.5 right-3"}`}
                                  style={{
                                    color: isOwnMessage
                                      ? "rgba(255,255,255,0.6)"
                                      : colors.textNeutralMuted,
                                  }}
                                >
                                  {formatTime(msg.created_at)}
                                  {isOwnMessage && (
                                    <span
                                      style={{
                                        color:
                                          ownMessageState === "read"
                                            ? colors.chatRightAccentColor
                                            : undefined,
                                      }}
                                    >
                                      {ownMessageState === "read"
                                        ? "✓✓"
                                        : ownMessageState === "delivered"
                                          ? "✓✓"
                                          : "✓"}
                                    </span>
                                  )}
                                </span>
                              </div>
                              {/* Hover action buttons */}
                              <div
                                className={`flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${activeReactionPicker === msg.message_id ? "!opacity-100" : ""}`}
                              >
                                <button
                                  onClick={() =>
                                    setActiveReactionPicker(
                                      activeReactionPicker === msg.message_id
                                        ? null
                                        : msg.message_id,
                                    )
                                  }
                                  className="p-1 rounded-full hover:bg-black/5 transition-colors"
                                  title="React"
                                >
                                  <Smile
                                    size={16}
                                    style={{ color: colors.textNeutralMuted }}
                                  />
                                </button>
                                <button
                                  onClick={() => setReplyToMessage(msg)}
                                  className="p-1 rounded-full hover:bg-black/5 transition-colors"
                                  title="Reply"
                                >
                                  <Reply
                                    size={16}
                                    style={{ color: colors.textNeutralMuted }}
                                  />
                                </button>
                              </div>
                            </div>
                            {/* Reaction picker popup */}
                            {activeReactionPicker === msg.message_id && (
                              <div
                                ref={reactionPickerRef}
                                className={`absolute z-20 flex items-center gap-1.5 px-2 py-1.5 rounded-full ${
                                  isOwnMessage ? "right-0" : "left-0"
                                }`}
                                style={{
                                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                                  backdropFilter: "blur(16px)",
                                  WebkitBackdropFilter: "blur(16px)",
                                  boxShadow:
                                    "0 4px 24px rgba(2, 2, 16, 0.12), 0 0 0 1px rgba(2, 2, 16, 0.05)",
                                  bottom: "100%",
                                  marginBottom: 6,
                                  animation:
                                    "scaleIn 0.15s cubic-bezier(0.16, 1, 0.3, 1) forwards",
                                  transformOrigin: isOwnMessage
                                    ? "bottom right"
                                    : "bottom left",
                                }}
                              >
                                {REACTION_OPTIONS.map((reaction) => (
                                  <button
                                    key={`${msg.message_id}-picker-${reaction}`}
                                    onClick={() => {
                                      sendReactionMutation.mutate({
                                        message_id: msg.message_id,
                                        reaction,
                                      });
                                      setActiveReactionPicker(null);
                                    }}
                                    className="w-9 h-9 flex items-center justify-center rounded-full text-lg transition-all duration-150 hover:scale-125 hover:bg-black/5 active:scale-100"
                                  >
                                    {reaction}
                                  </button>
                                ))}
                              </div>
                            )}
                            {/* Existing reactions */}
                            {groupedReactions.length > 0 && (
                              <div
                                className={`mt-1 flex flex-wrap gap-1 ${
                                  isOwnMessage ? "justify-end mr-1" : "ml-1"
                                }`}
                              >
                                {groupedReactions.map(([reaction, count]) => (
                                  <button
                                    key={`${msg.message_id}-${reaction}`}
                                    onClick={() =>
                                      sendReactionMutation.mutate({
                                        message_id: msg.message_id,
                                        reaction,
                                      })
                                    }
                                    className="px-2 py-0.5 rounded-full text-xs transition-opacity hover:opacity-80"
                                    style={{
                                      backgroundColor:
                                        colors.containerNeutralLightLowest,
                                      color: colors.textNeutralDefault,
                                    }}
                                  >
                                    {reaction} {count}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input - sticky at bottom */}
              <div className="sticky bottom-0 z-30 px-4 pt-3 pb-4 flex-shrink-0">
                {isInputLockedForApplicant && (
                  <div
                    className="flex items-center gap-3 px-4 py-3 mb-3 rounded-2xl"
                    style={{
                      backgroundColor: "rgba(91, 61, 246, 0.06)",
                      border: "1px solid rgba(91, 61, 246, 0.1)",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="flex-shrink-0"
                      style={{ color: colors.accentColor }}
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                    <p
                      className="text-sm m-0 leading-snug"
                      style={{ color: colors.textNeutralMuted }}
                    >
                      {t("chats_tab.waiting_for_flat_reply") ||
                        "The flat needs to send the first reply before you can message."}
                    </p>
                  </div>
                )}
                {/* Reply preview */}
                {replyToMessage && (
                  <div
                    className="flex items-center justify-between gap-2 mb-2.5 pl-3 pr-4 py-2.5 rounded-2xl overflow-hidden"
                    style={{
                      backgroundColor: colors.containerNeutralLightLowest,
                      backdropFilter: "blur(12px)",
                      WebkitBackdropFilter: "blur(12px)",
                    }}
                  >
                    <div
                      className="w-[3px] self-stretch flex-shrink-0 rounded-full"
                      style={{ backgroundColor: colors.accentColor }}
                    />
                    <div className="min-w-0 flex-1 pl-2">
                      <p
                        className="text-xs font-semibold m-0"
                        style={{ color: colors.accentColor }}
                      >
                        {selectedChat.users.find(
                          (u) => u.user_id === replyToMessage.sender_id,
                        )?.username || "Unknown"}
                      </p>
                      <p
                        className="text-sm truncate m-0 mt-0.5"
                        style={{ color: colors.textNeutralMuted }}
                      >
                        {replyToMessage.type === "application"
                          ? `📋 ${t("chats_tab.last_message_types.application")}`
                          : replyToMessage.type === "image"
                            ? "📷 Image"
                            : replyToMessage.type === "audio"
                              ? "🎤 Voice message"
                              : replyToMessage.type === "document"
                                ? "📄 Document"
                                : replyToMessage.type === "location"
                                  ? "📍 Location"
                                  : replyToMessage.message || "Message"}
                      </p>
                    </div>
                    <button
                      onClick={() => setReplyToMessage(null)}
                      className="p-1.5 rounded-full hover:bg-black/10 transition-colors flex-shrink-0"
                    >
                      <X size={14} style={{ color: colors.textNeutralMuted }} />
                    </button>
                  </div>
                )}
                <div
                  className="flex items-end gap-2 rounded-3xl pl-4 pr-1.5 py-1.5"
                  style={{
                    backgroundColor: colors.containerNeutralLightLowest,
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                  }}
                >
                  <textarea
                    value={messageInput}
                    onChange={(e) => {
                      setMessageInput(e.target.value);
                      e.target.style.height = "auto";
                      e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                        const target = e.target as HTMLTextAreaElement;
                        target.style.height = "auto";
                      }
                    }}
                    placeholder={
                      isInputLockedForApplicant
                        ? t("chats_tab.waiting_placeholder") ||
                          "Waiting for the flat to reply first..."
                        : t("chats_tab.type_message") || "Type a message..."
                    }
                    disabled={isInputLockedForApplicant}
                    rows={1}
                    className="flex-1 bg-transparent py-2 outline-none text-base disabled:opacity-40 resize-none"
                    style={{
                      color: colors.textNeutralDefault,
                      maxHeight: 120,
                    }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={
                      !messageInput.trim() ||
                      sendMessageMutation.isPending ||
                      isInputLockedForApplicant
                    }
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-30 disabled:hover:scale-100 flex-shrink-0"
                    style={{
                      backgroundColor: messageInput.trim()
                        ? colors.accentColor
                        : "rgba(2, 2, 16, 0.08)",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        color: messageInput.trim()
                          ? colors.textNeutralOnPrimary
                          : colors.textNeutralMuted,
                      }}
                    >
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatsPlaceholder;
